import type { ReactNode } from "react";
import type { ProductSummary } from "@/common/productSummary/types";
import { FormatSubType } from "@/constants/tagular/main";

export type Subcategory = {
  subcategoryLabel: string;
  productUuids: string[];
  products?: ProductSummary[] | null;
  /**
   * Optional full-label overrides for the accordion link text.
   * If provided, these are used verbatim (no concatenation).
   */
  viewMoreLabel?: string;
  showLessLabel?: string;
};

export type Category = {
  key:
    | "course"
    | "executiveEducation"
    | "certificate"
    | "masters"
    | "bachelors";
  productType: string;
  categoryLabel: string;
  subcategories?: Subcategory[] | null;
};

export type ProductCardTabsAndPillsByUUIDProps = {
  title?: string;
  /** Optional decorative content before the title (e.g. trending icon). */
  titleLeading?: ReactNode;
  className?: string;
  categories?: Category[];
  location?: string;
  cardFormatSubtype?: FormatSubType;
  /**
   * Override for Monarch-driven default title decor (flame). Only applies when feature flag
   * `NEXT_PUBLIC_FEATURE_HOMEPAGE_TRENDING_COURSE_UPDATE` is `test` and Monarch enables the flame.
   * - `undefined`: on if `location` is in `MONARCH_TRENDING_TITLE_DECOR_LOCATIONS` and Monarch `showTrendingFlameIcon` is true.
   * - `true` / `false`: force on / off regardless of `location` (still flag + Monarch gated).
   */
  monarchTrendingTitleDecor?: boolean;
};

/**
 * Analytics `location` values where the default Monarch-driven trending flame may appear.
 * Extend when adding placements; keep in sync with CMS/shortcode `location` props.
 */
export const MONARCH_TRENDING_TITLE_DECOR_LOCATIONS = new Set([
  "trending-products",
  "homepage",
]);

/** Default product-type tab order when Monarch does not supply `categoryKeyOrder`. */
export const TRENDING_ON_EDX_DEFAULT_CATEGORY_KEY_ORDER: Category["key"][] = [
  "course",
  "executiveEducation",
  "certificate",
  "bachelors",
  "masters",
];

export type TrendingOnEdxProductTabsVariants = "legacy" | "test";

export type TrendingOnEdxProductTabsData = {
  prioritizeCoursesFirst: boolean;
  /** Full tab order by category `key`. When set, this overrides CMS order (open courses should use `course`). */
  categoryKeyOrder?: Category["key"][];
  showTrendingFlameIcon: boolean;
};

export type TrendingOnEdxProductTabsResult = {
  isLoading: boolean;
  variant: TrendingOnEdxProductTabsVariants;
  data: TrendingOnEdxProductTabsData;
};
